/* Marketing & Brand — a faithful React port of the prototype's rMarketing screen.
 *
 * Each active product runs one campaign per quarter: an ad budget, a media
 * channel and a creative direction, optionally focused on a chosen group of
 * campaign countries. Channel × creative determine the segment "fit" (🎯) and the
 * projected next-quarter awareness gain per segment; concentrating the same
 * budget on fewer countries buys more awareness per buyer (budget focus). The
 * company-level brand strategy (Range vs Unique) decides whether campaigns build
 * one shared brand or a brand per product — switching it mid-game resets every
 * brand rating to zero. A per-country table shows your brand rating (and rivals')
 * everywhere you operate.
 *
 * Every lever is staged in the decisions draft (store.setProductMkt → d.products[]
 * .mkt, store.setBrandStrat → d.brandStrat) and previewed live through
 * buildWorkingGame, which replays the same intents the server's applyDecisions
 * runs at advance — so the fit %, awareness gains and brand ratings on screen are
 * exactly what resolves. Sub-sections are plain render functions (not nested
 * components) so React reconciles in place and the page never scroll-jumps. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import {
  Engine, applyGameCfg, SEGMENTS, AD_CHANNELS, CREATIVES, BRAND_STRATS,
  COUNTRIES, COMPETITORS, REGIONS, PROFILES, fmtM, fmtK, clamp,
} from '@venturewrist/engine';
import type { MarketingDecision } from '@venturewrist/shared';
import { useDecisions } from '../../store/decisions';
import { buildWorkingGame } from '../../lib/designState';
import { Flag } from '../../components/Flag';
import { EmptyNote } from '../../components/ui';
import type { ScreenProps } from './types';
import '../../styles/marketing.css';

const Eng = Engine as any;
const SEGS = SEGMENTS as any[];
const CHS = AD_CHANNELS as any[];
const CRS = CREATIVES as any[];
const STRATS = BRAND_STRATS as any[];
const PROFS = PROFILES as any;
const CTRYS = COUNTRIES as any[];
const COMPALL = COMPETITORS as any[];

const WhyChip = () => <span className="tooltip-why" title="Learn the theory">?</span>;

const Bar = ({ v, color }: { v: number; color?: string }) => (
  <div className="bar-track"><div className="bar-fill" style={{ width: clamp(v, 0, 100) + '%', background: color || undefined }} /></div>
);

function SegTag({ segId }: { segId: string }) {
  const s = SEGS.find((x) => x.id === segId);
  if (!s) return null;
  return <span className="seg-tag" style={{ background: s.color + '22', color: s.color }}>{s.icon} {s.name}</span>;
}

function GenChip({ st, label }: { st: string; label: string }) {
  const S = (Eng.STATUS as any)[st];
  if (!S) return null;
  return <span className="gen-chip" style={{ color: S.color }}>{S.icon} {label}</span>;
}

/* reach-profile micro-bars: one tiny bar per segment (height = match) — shows
 * peaky vs broad reach at a glance. Mirrors the prototype's `micro()`. */
function Micro({ c }: { c: any }) {
  return (
    <span className="micro">
      {SEGS.map((s) => <i key={s.id} style={{ height: Math.round(3 + c.match[s.id] * 12), background: s.color }} />)}
    </span>
  );
}

const chTip = (c: any) =>
  (c.covers ? 'Covers: ' + c.covers + ' — ' : '') + 'Reach: ' + SEGS.map((s) => s.icon + ' ' + Math.round(c.match[s.id] * 100)).join(' · ');

const fitClass = (m: number) => (m > 0.75 ? 'good' : m > 0.5 ? 'warn' : 'bad');
const methOf = (g: any, cc: string) => {
  const mm = (g.markets || {})[cc] || {};
  return '🌐' + (mm.dist ? ' 🤝' : '') + (mm.sub ? ' 🏪' + (mm.n || 0) + (mm.flagship ? '⭐' : '') : '') + (mm.carrier ? ' 📡' : '');
};

export function Marketing({ detail }: ScreenProps) {
  const draft = useDecisions((z) => z.draft);
  const setProductMkt = useDecisions((z) => z.setProductMkt);
  const setBrandStrat = useDecisions((z) => z.setBrandStrat);

  // display-only state (no game effect): per-product fit segment, brand table model, open dialog
  const [fitSegMap, setFitSegMap] = useState<Record<string, string>>({});
  const [bcoId, setBcoId] = useState<string | null>(null);
  const [campOpen, setCampOpen] = useState<string | null>(null);

  // Live working game: server state + staged edits, through the engine's mutators.
  const g = useMemo(() => buildWorkingGame(detail.state, draft), [detail.state, draft]);
  // Make the engine's ambient config (active countries, rival count) reflect this
  // game before any country-aware Engine.* call below. Cheap + idempotent.
  applyGameCfg(g);

  const round = g.round as number;
  const unique = Eng.isUnique(g) as boolean;
  const models = Eng.activeModels(g) as any[];

  if (models.length === 0) {
    return (
      <div className="mk-root">
        <EmptyNote>No products to market yet. Design one on the Design screen first.</EmptyNote>
      </div>
    );
  }

  // active countries + competitor set — mirrors the engine's ACTIVE()/COMPS().
  const active = g.countries && g.countries.length ? CTRYS.filter((c) => g.countries.includes(c.id)) : CTRYS;
  const nComps = g.numComps ? Math.max(1, Math.min(g.numComps, COMPALL.length)) : 3;
  const comps = COMPALL.slice(0, nComps);
  const wD = Eng.worldDemand(round);
  const totalBudget = models.reduce((a, p) => a + (p.mkt?.budget || 0), 0);
  const ctrl = Eng.ctrlFraction(g, round) as number;

  // effective MarketingDecision for a product (the working game already overlays
  // the draft) → the clean object we stage on every edit.
  const mktOf = (m: any): MarketingDecision => ({
    budget: m.mkt?.budget ?? 0,
    channel: m.mkt?.channel ?? CHS[0].id,
    creative: m.mkt?.creative ?? CRS[0].id,
    ...(Array.isArray(m.mkt?.countries) ? { countries: m.mkt.countries } : {}),
  });
  const update = (m: any, patch: Partial<MarketingDecision>) => setProductMkt(m.id, { ...mktOf(m), ...patch });
  const setCountries = (m: any, arr: string[]) => update(m, { countries: arr });

  // ── KPI strip ───────────────────────────────────────────────────────────────
  const kpiStrip = () => {
    const ctrlColor = ctrl > 0.8 ? 'var(--good)' : ctrl > 0.5 ? 'var(--warn)' : 'var(--bad)';
    return (
      <div className="grid3" style={{ marginBottom: 14 }}>
        <div className="card">
          <div className="sub">
            {unique
              ? <>Company brand loyalty <span style={{ opacity: 0.7 }}>(sales-weighted average of your product brands)</span></>
              : <>Brand loyalty <span style={{ opacity: 0.7 }}>(earned country by country via sales, quality &amp; satisfaction — not ad spend)</span></>}
          </div>
          <div className="big-num">{Math.round(g.brand)}<span className="sub"> /100</span></div>
          <Bar v={g.brand} />
        </div>
        <div className="card">
          <div className="sub">Total ad budget this qtr</div>
          <div className="big-num">{fmtM(totalBudget * 1e6)}</div>
        </div>
        <div className="card">
          <div className="sub">Marketing control<WhyChip /></div>
          <div className="big-num" style={{ color: ctrlColor }}>{Math.round(ctrl * 100)}%</div>
          <div className="sub" style={{ marginTop: 4 }}>
            of your reachable demand sees <b>your</b> campaigns. The rest is distributor-served: their generic push counts as
            just 30% awareness in your Brand Rating, and sales there build loyalty at half rate.
          </div>
        </div>
      </div>
    );
  };

  // ── brand strategy card ───────────────────────────────────────────────────────
  const brandStratCard = () => {
    const curStrat = g.brandStrat || 'range';
    const desc = unique
      ? "Each product has its own brand rating — campaigns build only the advertised product's brand, and new products start from scratch."
      : 'All products share one brand rating — every campaign builds the shared brand, which applies to all products.';
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="spread" style={{ flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h3 style={{ marginBottom: 4 }}>Brand strategy: {unique ? '🎫 Unique Brand' : '🏷️ Range Brand'}</h3>
            <div className="sub">{desc}</div>
          </div>
          <div className="row">
            {STRATS.map((b) => (
              <button key={b.id} className={'opt-pill' + (curStrat === b.id ? ' on' : '')} onClick={() => {
                if (curStrat === b.id) return;
                if (g.history?.length && !window.confirm('Switch to ' + b.name + '?\n\nYour products will be designated with the new brand strategy and ALL brand ratings will be reset to zero — loyalty and awareness, in every country, company and per-product. You will rebuild your brand from scratch.')) return;
                setBrandStrat(b.id);
              }}>
                {b.icon} {b.name}
              </button>
            ))}
          </div>
        </div>
        {g.history?.length ? (
          <div className="sub" style={{ marginTop: 8, color: 'var(--warn)' }}>
            ⚠ Switching strategy mid-game resets ALL brand ratings to zero — you would rebuild loyalty and awareness from scratch.
          </div>
        ) : null}
      </div>
    );
  };

  // ── per-product campaign card ──────────────────────────────────────────────────
  const productCard = (m: any) => {
    const ch = CHS.find((c) => c.id === m.mkt?.channel) || CHS[0];
    const cr = CRS.find((c) => c.id === m.mkt?.creative) || CRS[0];
    const matchFit = ch.match[m.targetSeg] * 0.55 + cr.match[m.targetSeg] * 0.45;
    const live = (Eng.liveGens(m) as any[]).slice().sort((a, b) => b.gen - a.gen);
    const pb = unique ? Eng.pBrand(g, m) : null;
    const fitSeg = fitSegMap[m.id] || m.targetSeg;
    const fsObj = SEGS.find((s) => s.id === fitSeg);
    const ccs = Eng.campaignCountries(m) as string[];
    const conc = Eng.campaignConc(m, round) as number;
    const selD = ccs.reduce((a, cc) => a + Eng.qDemand(Eng.country(cc), round), 0);
    const worldwide = ccs.length === active.length;
    const base = (m.mkt?.budget || 0) * 0.16 * conc;
    const targetName = SEGS.find((s) => s.id === m.targetSeg)?.name;

    return (
      <div className="card" style={{ marginBottom: 14 }} key={m.id}>
        <div className="spread">
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <b>{m.name}</b>
            <SegTag segId={m.targetSeg} />
            {live.length > 1 ? (
              <>
                <span className="sub">covers</span>
                {live.map((gv) => <GenChip key={gv.gen} st={Eng.genStatus(m, gv)} label={'Gen ' + gv.gen} />)}
              </>
            ) : null}
          </div>
          <span className={'chip ' + fitClass(matchFit)} title="🎯 segment fit — combined channel × creative reach of the target segment">
            🎯 {Math.round(matchFit * 100)}%
          </span>
        </div>

        {unique && pb ? (
          <div className="grid2" style={{ gap: 10, marginTop: 10 }}>
            <div>
              <div className="spread" style={{ fontSize: 11, marginBottom: 2 }}><span className="sub">This product's brand loyalty</span><b>{Math.round(pb.bLoyal)}</b></div>
              <Bar v={pb.bLoyal} />
            </div>
            <div>
              <div className="spread" style={{ fontSize: 11, marginBottom: 2 }}><span className="sub">Awareness in {targetName}</span><b>{Math.round(pb.bAw[m.targetSeg] * 100)}%</b></div>
              <Bar v={pb.bAw[m.targetSeg] * 100} />
            </div>
          </div>
        ) : null}

        <div className="slider-row" style={{ margin: '12px 0 8px' }} data-tut="marketing-budget">
          <span className="sub" style={{ width: 110 }}>Budget / quarter</span>
          <input type="range" min={0} max={6} step={0.25} value={m.mkt?.budget ?? 0}
            onChange={(e) => update(m, { budget: +e.target.value })} aria-label={m.name + ' ad budget'} />
          <b style={{ width: 64, textAlign: 'right' }}>{fmtM((m.mkt?.budget || 0) * 1e6)}</b>
        </div>

        <div className="row" style={{ margin: '0 0 12px', flexWrap: 'wrap', gap: 8 }}>
          <span className="sub" style={{ width: 110 }}>Campaign countries</span>
          <span className="chip acc">
            {worldwide ? '🌍 Worldwide · all ' + active.length : ccs.length + ' countr' + (ccs.length === 1 ? 'y' : 'ies') + ' · ' + Math.round(selD / wD * 100) + '% of world demand'}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setCampOpen(m.id)}>Select countries</button>
          {conc > 1.05 ? (
            <span className="chip" title="Media cost scales with audience size — the same budget concentrated on fewer countries buys more awareness per buyer there (capped ×4).">
              🔍 budget focus ×{conc.toFixed(1)}
            </span>
          ) : null}
        </div>

        <div className="row" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
          <span className="sub">Show 🎯 fit for:</span>
          {SEGS.map((s) => (
            <button key={s.id} className={'opt-pill' + (fitSeg === s.id ? ' on' : '')} style={{ padding: '3px 9px', fontSize: 11 }}
              onClick={() => setFitSegMap((prev) => ({ ...prev, [m.id]: s.id }))}>
              {s.icon} {s.name}{s.id === m.targetSeg ? ' ◎' : ''}
            </button>
          ))}
          <span className="sub" style={{ fontSize: 10.5 }}>◎ = target segment</span>
        </div>

        <div className="grid2" data-tut="marketing-channels">
          <div>
            <div className="sub" style={{ marginBottom: 6 }}>Media channel <span style={{ opacity: 0.7 }}>(bars = reach per segment)</span></div>
            <div className="opt-row">
              {CHS.map((c) => (
                <button key={c.id} className={'opt-pill' + (m.mkt?.channel === c.id ? ' on' : '')} title={chTip(c)} onClick={() => update(m, { channel: c.id })}>
                  <Micro c={c} />{c.icon} {c.name}
                  <span className="pc" title={'🎯 segment fit — how well this channel reaches ' + fsObj?.name}>🎯 {Math.round(c.match[fitSeg] * 100)}%</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="sub" style={{ marginBottom: 6 }}>Creative direction</div>
            <div className="opt-row">
              {CRS.map((c) => (
                <button key={c.id} className={'opt-pill' + (m.mkt?.creative === c.id ? ' on' : '')} onClick={() => update(m, { creative: c.id })}>
                  {c.name}
                  <span className="pc" title={'🎯 segment fit — how well this creative resonates with ' + fsObj?.name}>🎯 {Math.round(c.match[fitSeg] * 100)}%</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="sub" style={{ marginTop: 10 }} title="Projected awareness gain next quarter from this budget, channel and creative — before the ~20%/qtr decay. Applies only in the selected campaign countries.">
          Next-quarter awareness gain {worldwide ? '(worldwide)' : '(in your ' + ccs.length + ' campaign countr' + (ccs.length === 1 ? 'y' : 'ies') + ')'}:{' '}
          {SEGS.map((s) => {
            const pp = base * (ch.match[s.id] * 0.55 + cr.match[s.id] * 0.45) * (s.id === m.targetSeg ? 1 : 0.45) * 100;
            return <span key={s.id} className="chip" style={{ fontSize: 10.5, padding: '2px 8px', marginRight: 4, color: s.color }}>{s.icon} +{pp.toFixed(1)}pp</span>;
          })}
        </div>
      </div>
    );
  };

  // ── brand ratings by country ───────────────────────────────────────────────────
  const bcoModel = unique ? (models.find((p) => p.id === bcoId) || models[0]) : null;
  const brandByCountry = () => {
    const sorted = active.slice().sort((a, b) => Eng.qDemand(b, round) - Eng.qDemand(a, round));
    const ratingOf = (c: any) => {
      const e = unique ? (bcoModel ? Eng.pBrandCo(g, bcoModel)[c.id] : { loyal: 0, aw: {} }) : Eng.ensureBrandCo(g)[c.id];
      const prof = PROFS[c.profile];
      const awC = SEGS.reduce((a, s) => a + (e.aw[s.id] || 0) * prof[s.id], 0);
      return { br: Eng.brandRating(e.loyal, awC), loyal: e.loyal, awC };
    };
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="spread" style={{ flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>Brand ratings by country<WhyChip /></h3>
          {unique && models.length > 1 ? (
            <div className="row">
              {models.map((p) => (
                <button key={p.id} className={'opt-pill' + (bcoModel && p.id === bcoModel.id ? ' on' : '')} style={{ padding: '3px 9px', fontSize: 11 }} onClick={() => setBcoId(p.id)}>
                  {p.name}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <div className="sub" style={{ margin: '6px 0 10px' }}>
          Brand is built country by country: campaigns raise awareness only where they run, and loyalty is earned through local
          sales. Rating = 0.6 × loyalty + 40 × awareness (awareness weighted by each country's segment mix). Sorted by market
          size. Distribution methods: 🌐 online · 🤝 distributor · 🏪 own stores (count, ⭐ flagship) · 📡 carrier — manage them
          in the Distribution screen.
        </div>
        <div style={{ maxHeight: 340, overflowY: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Country</th>
                <th title="Your distribution methods here — manage in the Distribution screen">Your distribution</th>
                <th style={{ minWidth: 130 }}>Your rating</th>
                <th className="num">Loyalty</th>
                <th className="num">Awareness</th>
                {comps.map((k) => <th key={k.id} className="num" style={{ color: k.color }}>{k.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => {
                const { br, loyal, awC } = ratingOf(c);
                return (
                  <tr key={c.id}>
                    <td><b><Flag cc={c.id} country={c.name} />{c.name}</b></td>
                    <td>{methOf(g, c.id)}</td>
                    <td><div className="spread" style={{ gap: 8 }}><div className="bar-track" style={{ flex: 1, minWidth: 60 }}><div className="bar-fill" style={{ width: Math.round(br) + '%' }} /></div><b>{Math.round(br)}</b></div></td>
                    <td className="num">{Math.round(loyal)}</td>
                    <td className="num">{Math.round(awC * 100)}%</td>
                    {comps.map((k) => <td key={k.id} className="num" style={{ color: 'var(--mut)' }}>{Math.round(Eng.compRatingIn(k, c.id, round))}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {unique && bcoModel ? (
          <div className="sub" style={{ marginTop: 8 }}>Showing {bcoModel.name} — under Unique Brand each product builds its own per-country ratings.</div>
        ) : null}
      </div>
    );
  };

  // ── per-product / segment awareness breakdown ───────────────────────────────────
  const breakdownCard = () => {
    if (unique) {
      return (
        <div className="card">
          <h3>Per-product brand ratings</h3>
          <div className="grid2" style={{ gap: 10 }}>
            {models.map((m) => {
              const pb = Eng.pBrand(g, m);
              const s = SEGS.find((x) => x.id === m.targetSeg);
              const br = Eng.brandRating(pb.bLoyal, pb.bAw[m.targetSeg]);
              return (
                <div key={m.id}>
                  <div className="spread" style={{ fontSize: 12, marginBottom: 3 }}><span>{s.icon} {m.name}</span><b>{Math.round(br)}</b></div>
                  <Bar v={br} color={s.color} />
                  <div className="sub" style={{ fontSize: 10.5, marginTop: 2 }}>loyalty {Math.round(pb.bLoyal)} · awareness {Math.round(pb.bAw[m.targetSeg] * 100)}% ({s.name})</div>
                </div>
              );
            })}
          </div>
          <div className="sub" style={{ marginTop: 10 }}>
            Under <b>Unique Brand</b>, each product's campaign builds only its own awareness (decaying ~20%/qtr without spend),
            and its loyalty is earned only by its own sales, quality and satisfaction. New products start from scratch.
          </div>
        </div>
      );
    }
    return (
      <div className="card">
        <h3>Segment awareness <span className="sub" style={{ fontWeight: 400 }}>(shared — your range brand applies to all products)</span></h3>
        <div className="grid2" style={{ gap: 10 }}>
          {SEGS.map((s) => (
            <div key={s.id}>
              <div className="spread" style={{ fontSize: 12, marginBottom: 3 }}><span>{s.icon} {s.name}</span><b>{Math.round((g.awareness[s.id] || 0) * 100)}%</b></div>
              <Bar v={(g.awareness[s.id] || 0) * 100} color={s.color} />
            </div>
          ))}
        </div>
        <div className="sub" style={{ marginTop: 10 }}>
          World demand-weighted average — see the per-country table above for where it actually sits. Awareness decays ~20% per
          quarter without spend and is only built in the countries a campaign runs in. Every product's campaign feeds this one
          shared brand.
        </div>
      </div>
    );
  };

  // ── campaign-countries dialog ───────────────────────────────────────────────────
  const campaignModal = () => {
    const m = models.find((x) => x.id === campOpen);
    if (!m) return null;
    const sel = new Set(Eng.campaignCountries(m) as string[]);
    const worldwide = sel.size === active.length;
    const selD = [...sel].reduce((a, cc) => a + Eng.qDemand(Eng.country(cc), round), 0);
    const conc = Eng.campaignConc(m, round) as number;

    const commit = (arr: string[]) => { if (arr.length) setCountries(m, arr); };
    const toggle = (cc: string) => {
      if (sel.has(cc)) { if (sel.size <= 1) return; sel.delete(cc); } else sel.add(cc);
      commit([...sel]);
    };
    const region = (rid: string, on: boolean) => {
      for (const c of active) if (c.region === rid) { if (on) sel.add(c.id); else sel.delete(c.id); }
      commit([...sel]);
    };
    const preset = (kind: 'all' | 'paid' | 'top10') => {
      let arr: string[];
      if (kind === 'all') arr = active.map((c) => c.id);
      else if (kind === 'paid') { arr = active.filter((c) => Eng.hasPaid((g.markets || {})[c.id])).map((c) => c.id); if (!arr.length) return; }
      else arr = active.slice().sort((a, b) => Eng.qDemand(b, round) - Eng.qDemand(a, round)).slice(0, 10).map((c) => c.id);
      commit(arr);
    };

    return (
      <div className="mk-modal-bg" onClick={() => setCampOpen(null)}>
        <div className="mk-modal" onClick={(e) => e.stopPropagation()}>
          <div className="mk-modal-head">
            <h3>📣 Campaign countries — {m.name}</h3>
            <button className="mk-modal-x" onClick={() => setCampOpen(null)} aria-label="Close">✕</button>
          </div>
          <div className="mk-modal-body">
            <p className="sub" style={{ marginBottom: 10 }}>
              Run {m.name}'s campaign in a group of countries. Media cost scales with audience size: the same budget concentrated
              on fewer countries buys more awareness per buyer there (capped ×4) — and builds none anywhere else.
            </p>
            <div className="row" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => preset('all')}>🌍 Worldwide</button>
              <button className="btn btn-ghost btn-sm" onClick={() => preset('paid')}>🤝 My paid markets</button>
              <button className="btn btn-ghost btn-sm" onClick={() => preset('top10')}>🏆 Top 10 markets</button>
            </div>
            {REGIONS.map((r: any) => {
              const cs = active.filter((c) => c.region === r.id);
              if (!cs.length) return null;
              const allOn = cs.every((c) => sel.has(c.id));
              return (
                <div key={r.id} style={{ marginBottom: 10 }}>
                  <div className="spread" style={{ marginBottom: 6 }}>
                    <b style={{ fontSize: 12 }}>{r.name}</b>
                    <button className="btn btn-ghost btn-sm" style={{ padding: '2px 9px', fontSize: 10.5 }} onClick={() => region(r.id, !allOn)}>
                      {allOn ? 'clear region' : 'select region'}
                    </button>
                  </div>
                  <div className="opt-row">
                    {cs.map((c) => (
                      <button key={c.id} className={'opt-pill' + (sel.has(c.id) ? ' on' : '')} style={{ padding: '3px 9px', fontSize: 11 }} onClick={() => toggle(c.id)}>
                        <Flag cc={c.id} country={c.name} />{c.name}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="card" style={{ marginTop: 4 }}>
              <div className="spread">
                <b>{worldwide ? '🌍 Worldwide — all ' + active.length + ' countries' : sel.size + ' countr' + (sel.size === 1 ? 'y' : 'ies') + ' selected · ' + Math.round(selD / wD * 100) + '% of world demand'}</b>
                <span className={'chip' + (conc > 1.05 ? ' acc' : '')} title="Awareness-gain multiplier in the selected countries">budget focus ×{conc.toFixed(1)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mk-root">
      <div className="screen-head">
        <div className="spread" style={{ flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h2>📣 Marketing &amp; Brand<WhyChip /></h2>
            <div className="sub">Each product runs one campaign per quarter. Channel and creative must match the target segment — consistency compounds<WhyChip /></div>
          </div>
          <span className="chip" title="🎯 = segment fit — how well this channel/creative reaches the selected segment">🎯 = segment fit</span>
        </div>
      </div>

      {brandStratCard()}
      {kpiStrip()}
      {models.map(productCard)}
      {brandByCountry()}
      {breakdownCard()}

      {campOpen ? campaignModal() : null}
    </div>
  );
}
